import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import swal from "sweetalert";
import Swal from "sweetalert2";
import axios from "axios";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { AdVideoExamplePrompt } from "./AdVideoExamplePrompt";

import "./AdVideoAi.css";

/* =====================================================
   CONSTANTS
===================================================== */

const API_BASE = "https://backend.whalesai.com/api/v1";
const AD_VIDEO_API = `${API_BASE}/advertisement-video-ai`;

const ACTIVE_STORAGE_KEY = "active_ad_video_ai";
const UNVIEWED_STORAGE_KEY = "unviewed_ad_video_ai";

// Page state that survives a reload (inputs + generated advertisement).
// const PERSIST_KEY = "ad_video_ai_page_state";
// const TAB_KEY = "ad_video_ai_active_tab";

// Image generation completion is normally pushed over the WebSocket. Safari
// can drop or suspend that socket, so we also poll the status endpoint.
const IMAGE_POLL_MS = 4000;
const IMAGE_TIMEOUT_MS = 6 * 60 * 1000;

// Safari can leave a request hanging forever after the tab was suspended.
// Without a timeout the "already checking" flags would never be released
// and polling would silently stop.
const STATUS_REQUEST_TIMEOUT_MS = 15000;

const formQuestions = [
    {
        key: "promoting",
        label: "What are you promoting?",
        placeholder: "e.g. Smart Home Automation System",
        required: true,
    },
    {
        key: "targetAudience",
        label: "Who is your target audience?",
        placeholder: "e.g. Busy professionals and families",
        required: true,
    },
    {
        key: "keyMessage",
        label: "What is the key message?",
        placeholder: "e.g. Control your home with your voice",
        required: true,
    },
    {
        key: "action",
        label: "What action should viewers take?",
        placeholder: "e.g. Visit our website for a free demo",
        required: true,
    },
    {
        key: "visualStyle",
        label: "What visual style do you want?",
        placeholder: "e.g. Futuristic, cinematic and cozy",
        required: true,
    },
    {
        key: "specialInstructions",
        label: "Any special instructions?",
        placeholder: "e.g. Focus on family interactions",
        required: false,
        isTextarea: true,
    },
];

const initialFormState = {
    promoting: "",
    targetAudience: "",
    keyMessage: "",
    action: "",
    visualStyle: "",
    specialInstructions: "",
};

/* =====================================================
   HELPERS
===================================================== */

const formatKey = (key = "") =>
    String(key)
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

const stringifyValue = (value, space) =>
    typeof value === "object" && value !== null
        ? JSON.stringify(value, null, space)
        : String(value ?? "—");

const formatImagePromptText = (prompts) => {
    if (prompts == null) return "";

    let parsedPrompts = prompts;

    if (typeof parsedPrompts === "string") {
        const trimmed = parsedPrompts.trim();

        if (!trimmed) return "";

        try {
            parsedPrompts = JSON.parse(trimmed);
        } catch {
            return trimmed;
        }
    }

    const promptList = Array.isArray(parsedPrompts)
        ? parsedPrompts
        : [parsedPrompts];

    return promptList
        .map((item, index) => {
            if (typeof item === "string") {
                const trimmed = item.trim();

                if (!trimmed) return "";

                try {
                    const parsed = JSON.parse(trimmed);

                    if (parsed && typeof parsed === "object") {
                        item = parsed;
                    } else {
                        return trimmed;
                    }
                } catch {
                    return trimmed;
                }
            }

            if (item == null) return "";

            if (typeof item !== "object") {
                return String(item);
            }

            const name =
                item.character_name ||
                item.location_name ||
                item.name ||
                item.title ||
                `Prompt ${index + 1}`;

            const text =
                item.image_prompt ||
                item.prompt ||
                item.visual_prompt ||
                item.description ||
                item.content ||
                item.imagePrompt ||
                item.text;

            if (typeof text === "string" && text.trim()) {
                return `${name}\n${text.trim()}`;
            }
            return JSON.stringify(item, null, 2);
        })
        .filter(Boolean)
        .join("\n\n");
};

const isVideoPromptKey = (key) =>
    formatKey(key).toLowerCase() === "video prompt";

const hasActiveTransactionInStorage = () => {
    try {
        const raw = localStorage.getItem(ACTIVE_STORAGE_KEY);
        if (!raw) return false;

        return Boolean(JSON.parse(raw)?.transactionId);
    } catch {
        return false;
    }
};

const getToken = () => localStorage.getItem("aiUser");

const apiHeaders = () => ({
    authorization: `Bearer ${getToken()}`,
});

const debugResponse = (label, response) => {
    console.groupCollapsed(`%c[AD VIDEO AI] ${label}`, "color:#22d3ee;font-weight:700;");
    console.log("Status:", response?.status);
    console.log("Success:", response?.data?.success);
    console.log("Message:", response?.data?.message);
    console.log("Transaction ID:", response?.data?.transactionId);
    console.log("Response:", response?.data);
    console.groupEnd();
};

const debugError = (label, error) => {
    console.groupCollapsed(`%c[AD VIDEO AI] ${label}`, "color:#ef4444;font-weight:700;");
    console.error("Message:", error?.message);
    console.error("Response:", error?.response?.data);
    console.error("Status:", error?.response?.status);
    console.error("Full Error:", error);
    console.groupEnd();
};

const buildCharacterItems = (characters) => {
    if (!characters) return [];

    const list = Array.isArray(characters)
        ? characters
        : Object.entries(characters).map(([key, value]) => ({
            ...(value || {}),
            _key: key,
        }));

    return list.map((character, index) => ({
        id: character?._key ?? index,
        name:
            character?.character_name ||
            character?.name ||
            character?._key ||
            `Character ${index + 1}`,
        subtitle: `Character ${index + 1}`,
        badge: null,
        fields: Object.entries(character || {}).filter(
            ([key]) => !["character_name", "name", "_key"].includes(key)
        ),
    }));
};

const buildLocationItems = (locations) => {
    if (!locations) return [];

    let list = [];

    if (Array.isArray(locations)) {
        list = locations.map((location, index) => ({
            ...location,
            _index: index,
        }));
    } else {
        const primary = locations.primary_locations || {};
        const supporting = locations.supporting_locations || {};

        Object.entries(primary).forEach(([key, value]) => {
            list.push({ ...(value || {}), _key: key, _type: "Primary" });
        });

        Object.entries(supporting).forEach(([key, value]) => {
            list.push({ ...(value || {}), _key: key, _type: "Supporting" });
        });
    }

    return list.map((location, index) => {
        const type =
            location?.type || location?.location_type || location?._type;

        return {
            id: location?._key ?? location?._index ?? index,
            name:
                location?.location_name ||
                location?.name ||
                location?._key ||
                `Location ${index + 1}`,
            subtitle: null,
            badge: type ? String(type) : null,
            fields: Object.entries(location || {}).filter(
                ([key]) =>
                    ![
                        "location_name",
                        "name",
                        "type",
                        "location_type",
                        "_key",
                        "_index",
                        "_type",
                    ].includes(key)
            ),
        };
    });
};

// Video generation per scene is not available yet (button is disabled).
const handleGenerateSceneVideo = (scene) => {
    // Future implementation:
    // 1. Read this scene's video_prompt.
    // 2. Send only this prompt to the video API.
    // 3. Track this scene's generation status.
    // 4. Display the generated video in this scene's card.
    console.log("Generate video for scene:", scene?.scene_number);
    console.log("scene title:", scene?.scene_title);
    console.log("scene duration:", scene?.duration_seconds);

    swal({
        title: "Feature Disabled",
        text: "This feature is currently disabled.",
        icon: "info",
        button: "Okay",
        className: "modal_class_success",
    });
};

/* =====================================================
   SMALL PRESENTATIONAL COMPONENTS
===================================================== */

function LoadingState() {
    return (
        <section className="advideo-loading-card" aria-live="polite">
            <div className="advideo-loading-orb">
                <AutoAwesomeIcon />
            </div>

            <h3 className="advideo-loading-title">
                AI is Creating Your Advertisement
            </h3>

            <p className="advideo-loading-text">
                Your Advertisement is being generated in the background.
                You can safely return later. Once ready, it will
                also be available from your Usage page.
            </p>

            <div className="advideo-ai-facts va-ai-facts">
                <AiFactsDisplay />
            </div>
        </section>
    );
}

function EmptyState() {
    return (
        <section className="advideo-empty-state">
            <div className="advideo-empty-icon" aria-hidden="true">✦</div>

            <h3>Your advertisement blueprint will appear here</h3>
            <p>
                Answer the questions above and let AI build the complete
                advertisement structure for you.
            </p>

            <div className="advideo-empty-features">
                <span>Character Bible</span>
                <span>Location Bible</span>
                <span>Story Structure</span>
                <span>Scene Breakdown</span>
                <span>Video Prompts</span>
            </div>
        </section>
    );
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text || "");

            setCopied(true);

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setCopied(false);
            }, 1800);
        } catch (error) {
            console.error("[AD VIDEO AI] Copy failed:", error);
        }
    };

    return (
        <button
            type="button"
            className={`advideo-copy-btn ${copied ? "is-copied" : ""}`}
            onClick={copy}
        >
            {copied ? "✓ Copied" : "Copy"}
        </button>
    );
}

function AspectRatioToggle({ aspectRatio, setAspectRatio, disabled }) {
    return (
        <button
            type="button"
            className="advideo-ratio-toggle"
            onClick={() =>
                setAspectRatio((prev) => (prev === "16:9" ? "9:16" : "16:9"))
            }
            disabled={disabled}
            aria-label={`Change aspect ratio. Current ratio ${aspectRatio}`}
        >
            <span className="advideo-ratio-icon" aria-hidden="true">
                {aspectRatio === "16:9" ? "▰" : "▯"}
            </span>
            {aspectRatio}
            <span className="advideo-ratio-switch" aria-hidden="true">⇄</span>
        </button>
    );
}

/* =====================================================
   CHARACTER / LOCATION BIBLE
===================================================== */

function BibleSection({
    title,
    items,
    kind,
    buttonLabel,
    onGenerate,
    generating,
    image,
    aspectRatio,
    setAspectRatio,
    prompt,
    showPromptModal,
    setShowPromptModal,
}) {
    if (!items.length) return null;

    return (
        <section className="advideo-card">
            <div className="advideo-card-head">
                <div>
                    <span className="advideo-kicker">PRODUCTION</span>
                    <h3>{title}</h3>
                </div>

                <div className="advideo-bible-actions">
                    <AspectRatioToggle
                        aspectRatio={aspectRatio}
                        setAspectRatio={setAspectRatio}
                        disabled={generating}
                    />

                    <Button
                        variant="contained"
                        size="small"
                        className="advideo-btn advideo-btn-primary"
                        onClick={onGenerate}
                        disabled={generating}
                    >
                        {generating ? "Generating..." : buttonLabel}
                    </Button>
                </div>
            </div>

            <div className="advideo-entity-grid">
                {items.map((item, index) => (
                    <article
                        className="advideo-entity-card"
                        key={`${item.id}-${index}`}
                    >
                        <header className="advideo-entity-head">
                            <h4>{item.name}</h4>

                            {item.subtitle && <span>{item.subtitle}</span>}

                            {item.badge && (
                                <span
                                    className={`advideo-location-type ${item.badge.toLowerCase() === "primary"
                                        ? "primary"
                                        : "supporting"
                                        }`}
                                >
                                    {item.badge}
                                </span>
                            )}
                        </header>

                        <dl className="advideo-detail-list">
                            {item.fields.map(([key, value]) => (
                                <div className="advideo-detail-row" key={key}>
                                    <dt>{formatKey(key)}</dt>
                                    <dd>{stringifyValue(value)}</dd>
                                </div>
                            ))}
                        </dl>
                    </article>
                ))}
            </div>
            {/* <div className="advideo-prompt-action">
                <button
                    type="button"
                    className="advideo-show-prompt-btn"
                    onClick={() => setShowPromptModal(true)}
                >
                    <span aria-hidden="true">✦</span>
                    Show Prompt
                </button>
            </div> */}

            {generating && (
                <div className="advideo-generating" role="status">
                    <span className="advideo-spinner" aria-hidden="true" />
                    <span>Generating {kind}</span>
                </div>
            )}

            {!generating && image && (
                <figure className="advideo-image-frame">
                    <img
                        src={image}
                        alt={`Generated ${kind}`}
                        onError={(error) =>
                            console.error(
                                `${kind} image failed to load:`,
                                error
                            )
                        }
                    />
                </figure>
            )}

            {!generating && (
                <div className="advideo-prompt-action">
                    <button
                        type="button"
                        className="advideo-show-prompt-btn"
                        onClick={() => setShowPromptModal(true)}
                    >
                        {/* <span aria-hidden="true">✦</span> */}
                        Show Prompt
                    </button>
                </div>
            )}


            {showPromptModal && (
                <div
                    className="advideo-prompt-modal-overlay"
                    onClick={() => setShowPromptModal(false)}
                >
                    <div
                        className="advideo-prompt-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`advideo-prompt-title-${kind}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="advideo-prompt-modal-header">
                            <div>
                                <span className="advideo-prompt-modal-kicker">
                                    PRODUCTION PROMPT
                                </span>
                                <h3 id={`advideo-prompt-title-${kind}`}>
                                    {title} Prompt
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="advideo-prompt-modal-copy"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(prompt || "");
                                        console.log("Prompt copied successfully!");
                                        swal({
                                            text: "Prompt copied successfully!",
                                            icon: "success",
                                            button: "OK",
                                            className: "modal_class_success"
                                        });
                                    } catch (error) {
                                        console.error("Failed to copy prompt:", error);
                                        swal({
                                            text: "Failed to copy prompt:",
                                            icon: "error",
                                            button: "OK",
                                            className: "modal_class_success"
                                        });
                                    }
                                }}
                                disabled={!prompt?.trim()}
                            >
                                <i className="bi bi-clipboard"></i>
                                <span>Copy</span>
                            </button>
                        </div>
                        <div className="advideo-prompt-modal-body">
                            <pre className="advideo-prompt-text">
                                {prompt?.trim()
                                    ? prompt
                                    : "No image prompt is available for this section yet."}
                            </pre>
                        </div>

                        <div className="advideo-prompt-modal-footer">
                            <button
                                type="button"
                                className="advideo-prompt-close-btn"
                                onClick={() => setShowPromptModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}



        </section>
    );
}

function CharacterBible({
    characters,
    onGenerate,
    generating,
    characterImage,
    aspectRatio,
    setAspectRatio,
    prompt,
    showPromptModal,
    setShowPromptModal,
}) {
    return (
        <BibleSection
            title="Character Bible"
            kind="character"
            items={buildCharacterItems(characters)}
            buttonLabel="Generate Character"
            onGenerate={() => onGenerate("character")}
            generating={generating}
            image={characterImage}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            prompt={prompt}
            showPromptModal={showPromptModal}
            setShowPromptModal={setShowPromptModal}
        />
    );
}

function LocationBible({
    locations,
    onGenerate,
    generating,
    locationImage,
    aspectRatio,
    setAspectRatio,
    prompt,
    showPromptModal,
    setShowPromptModal,
}) {
    return (
        <BibleSection
            title="Location Bible"
            kind="location"
            items={buildLocationItems(locations)}
            buttonLabel="Generate Location"
            onGenerate={() => onGenerate("location")}
            generating={generating}
            image={locationImage}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            prompt={prompt}
            showPromptModal={showPromptModal}
            setShowPromptModal={setShowPromptModal}
        />
    );
}

/* =====================================================
   STORY
===================================================== */

function StoryStructure({ story }) {
    if (!story) return null;

    return (
        <section className="advideo-card">
            <div className="advideo-card-head">
                <div>
                    <span className="advideo-kicker">NARRATIVE</span>
                    <h3>Story Structure</h3>
                </div>
            </div>

            <div className="advideo-story-grid">
                {Object.entries(story).map(([key, value]) => (
                    <div className="advideo-story-item" key={key}>
                        <span>{formatKey(key)}</span>
                        <div>{stringifyValue(value, 2)}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* =====================================================
   SCENES
===================================================== */

function SceneCard({
    scene,
    production = false,
    sceneAspectRatios,
    setSceneAspectRatios,
    activeTab
}) {
    const sceneNumber = scene?.scene_number;

    const displayFields = Object.entries(scene || {}).filter(
        ([key]) => !["scene_number", "scene_title"].includes(key)
    );

    const hasVideoPrompt = displayFields.some(([key]) =>
        isVideoPromptKey(key)
    );

    const sceneText = displayFields
        .map(
            ([key, value]) =>
                `${formatKey(key)}: ${typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value ?? "")
                }`
        )
        .join("\n\n");

    const videoAspectRatio = sceneAspectRatios?.[sceneNumber] || "16:9";

    const setVideoAspectRatio = (ratio) => {
        setSceneAspectRatios((prev) => ({
            ...prev,
            [sceneNumber]: ratio,
        }));
    };

    const getValueClass = (key) => {
        if (isVideoPromptKey(key)) return "advideo-video-prompt";
        return production
            ? "advideo-scene-value is-production"
            : "advideo-scene-value";
    };

    return (
        <article className="advideo-scene-card">
            <header className="advideo-scene-header">
                <div className="advideo-scene-number">
                    {sceneNumber || "—"}
                </div>

                <div className="advideo-scene-heading">
                    <h4>{scene?.scene_title || "Untitled Scene"}</h4>

                    {scene?.duration_seconds && (
                        <span>{scene.duration_seconds}s</span>
                    )}
                </div>

                <div className="advideo-scene-header-actions">

                    {hasVideoPrompt && (
                        <div className="advideo-video-aspect-control">
                            <label htmlFor={`video-aspect-${sceneNumber}`}>
                                Aspect Ratio
                            </label>
                            <select
                                id={`video-aspect-${sceneNumber}`}
                                className="advideo-aspect-select"
                                value={videoAspectRatio}
                                onChange={(e) =>
                                    setVideoAspectRatio(e.target.value)
                                }
                                aria-label={`Video aspect ratio for scene ${sceneNumber}`}
                            >
                                <option value="16:9">16:9</option>
                                <option value="9:16">9:16</option>
                            </select>
                        </div>
                    )}
                </div>
            </header>

            <div className="advideo-scene-body">
                {displayFields.map(([key, value]) => (
                    <div className="advideo-scene-field" key={key}>
                        {
                            activeTab == "prompts" && formatKey(key) == "Video prompt" ?
                                <div
                                    className=""
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "4px"
                                    }}
                                >
                                    <div className="advideo-scene-field-label">
                                        {formatKey(key)}
                                    </div>
                                    <CopyButton
                                        text={`Scene ${sceneNumber || ""} — ${scene?.scene_title || ""
                                            }\n\n${sceneText}`}
                                    />
                                </div>
                                :
                                <div className="advideo-scene-field-label">
                                    {formatKey(key)}
                                </div>
                        }

                        <div className={getValueClass(key)}>
                            {isVideoPromptKey(key)
                                ? String(value ?? "—").replace(
                                    /Generate\s+(?:16:9|9:16)\s+video/gi,
                                    `Generate ${videoAspectRatio} video`
                                )
                                : stringifyValue(value, 2)}
                        </div>
                    </div>
                ))}
            </div>

            {
                activeTab == "prompts" &&

                <footer className="advideo-scene-footer">
                    <button
                        type="button"
                        className="advideo-generate-video-btn"
                        onClick={() => handleGenerateSceneVideo(scene)}
                        disabled
                        title="Coming soon"
                    >
                        Generate Video
                    </button>
                </footer>
            }

            {
                activeTab == "scenes" &&

                <footer className="advideo-scene-footer">
                    <button
                        type="button"
                        className="advideo-generate-video-btn"
                        // onClick={() => handleGenerateSceneVideo(scene)}
                        disabled
                        title="Coming soon"
                    >
                        Generate Storyboard
                    </button>
                </footer>
            }
        </article>
    );
}

function SceneList({
    scenes,
    extraKey,
    production = false,
    emptyText,
    sceneAspectRatios,
    setSceneAspectRatios,
    activeTab
}) {
    const listRef = useRef(null);

    if (!Array.isArray(scenes) || !scenes.length) {
        return <div className="advideo-no-data">{emptyText}</div>;
    }

    const scrollList = (direction) => {
        const el = listRef.current;
        if (!el) return;

        el.scrollBy({
            left: direction * Math.max(el.clientWidth * 0.8, 300),
            behavior: "smooth",
        });
    };

    return (
        <div className="advideo-scenes-wrap">
            <div className="advideo-scenes-nav">
                <span>{scenes.length} scenes</span>

                <div>
                    <button
                        type="button"
                        onClick={() => scrollList(-1)}
                        aria-label="Scroll scenes left"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollList(1)}
                        aria-label="Scroll scenes right"
                    >
                        ›
                    </button>
                </div>
            </div>

            <div className="advideo-scenes-list" ref={listRef}>
                {scenes.map((scene, index) => (
                    <SceneCard
                        key={scene?.scene_number || index}
                        production={production}
                        scene={{
                            scene_number: scene?.scene_number || index + 1,
                            scene_title:
                                scene?.scene_title || `Scene ${index + 1}`,
                            duration_seconds: scene?.duration_seconds,
                            [extraKey]: scene?.[extraKey],
                        }}
                        sceneAspectRatios={sceneAspectRatios}
                        setSceneAspectRatios={setSceneAspectRatios}
                        activeTab={activeTab}
                    />
                ))}
            </div>
        </div>
    );
}

/* =====================================================
   PROMPT COLLECTION
===================================================== */

function PromptCollection({ title, prompts, type = "prompt" }) {
    if (!Array.isArray(prompts) || !prompts.length) {
        return null;
    }

    const getPromptData = (prompt) => {
        if (typeof prompt === "string") {
            try {
                const parsed = JSON.parse(prompt);

                if (parsed && typeof parsed === "object") {
                    return parsed;
                }
            } catch {
                // Keep normal text prompts as they are.
            }

            return {
                name: "",
                prompt: prompt,
            };
        }

        if (prompt && typeof prompt === "object") {
            return prompt;
        }

        return {
            name: "",
            prompt: String(prompt ?? ""),
        };
    };

    const getPromptName = (data, index) => {
        return (
            data.character_name ||
            data.location_name ||
            data.scene_title ||
            data.name ||
            `${formatKey(type)} ${index + 1}`
        );
    };

    const getPromptText = (data) => {
        return (
            data.image_prompt ||
            data.video_prompt ||
            data.prompt ||
            data.description ||
            data.content ||
            ""
        );
    };

    return (
        <section className="advideo-card advideo-prompt-collection">
            <div className="advideo-card-head">
                <div>
                    <span className="advideo-kicker">AI GENERATED</span>
                    <h3>{title}</h3>
                </div>

                <span className="advideo-count-badge">
                    {prompts.length}{" "}
                    {prompts.length === 1 ? "Prompt" : "Prompts"}
                </span>
            </div>

            <div className="advideo-prompt-list">
                {prompts.map((prompt, index) => {
                    const data = getPromptData(prompt);
                    const name = getPromptName(data, index);
                    const promptText = getPromptText(data);

                    // Preserve the original object/string for copying.
                    const copyText =
                        typeof prompt === "string"
                            ? (() => {
                                try {
                                    return JSON.stringify(
                                        JSON.parse(prompt),
                                        null,
                                        2
                                    );
                                } catch {
                                    return prompt;
                                }
                            })()
                            : JSON.stringify(prompt, null, 2);

                    return (
                        <article
                            className="advideo-prompt-card advideo-prompt-card-custom"
                            key={`${name}-${index}`}
                        >
                            <div className="advideo-prompt-number">
                                {String(index + 1).padStart(2, "0")}
                            </div>

                            <div className="advideo-prompt-content">
                                <div className="advideo-prompt-top">
                                    <div className="advideo-prompt-heading">
                                        <span className="advideo-prompt-type">
                                            {formatKey(type)} Prompt
                                        </span>

                                        <h4 className="advideo-prompt-name">
                                            {name}
                                        </h4>
                                    </div>

                                    <CopyButton text={copyText} />
                                </div>

                                <div className="advideo-prompt-description">
                                    {promptText || "No prompt text available."}
                                </div>

                                {Object.entries(data).some(
                                    ([key]) =>
                                        ![
                                            "character_name",
                                            "location_name",
                                            "scene_title",
                                            "name",
                                            "image_prompt",
                                            "video_prompt",
                                            "prompt",
                                            "description",
                                            "content",
                                        ].includes(key)
                                ) && (
                                        <details className="advideo-prompt-details">
                                            <summary>View additional details</summary>

                                            <div className="advideo-prompt-extra">
                                                {Object.entries(data)
                                                    .filter(
                                                        ([key]) =>
                                                            ![
                                                                "character_name",
                                                                "location_name",
                                                                "scene_title",
                                                                "name",
                                                                "image_prompt",
                                                                "video_prompt",
                                                                "prompt",
                                                                "description",
                                                                "content",
                                                            ].includes(key)
                                                    )
                                                    .map(([key, value]) => (
                                                        <div
                                                            className="advideo-prompt-meta"
                                                            key={key}
                                                        >
                                                            <span>
                                                                {formatKey(key)}
                                                            </span>

                                                            <strong>
                                                                {typeof value ===
                                                                    "object"
                                                                    ? JSON.stringify(
                                                                        value
                                                                    )
                                                                    : String(
                                                                        value ??
                                                                        "—"
                                                                    )}
                                                            </strong>
                                                        </div>
                                                    ))}
                                            </div>
                                        </details>
                                    )}
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}


/* =====================================================
   RESULT DASHBOARD
===================================================== */

function ResultDashboard({
    resultData,
    onGenerateBibleImage,
    generatingCharacter,
    generatingLocation,
    characterImage,
    locationImage,
    aspectRatio,
    setAspectRatio,
    sceneAspectRatios,
    setSceneAspectRatios,
    showPromptModal,
    setShowPromptModal
}) {
    const [activeTab, setActiveTab] = useState("characters");

    const claudeData = resultData?.claudePrompt || {};
    const gptData = resultData?.gptPrompt || {};

    const characters =
        claudeData.characters ||
        claudeData.character_bible ||
        gptData.characters;

    const locations =
        claudeData.locations ||
        claudeData.location_bible ||
        gptData.locations;

    const story = claudeData.story_structure || gptData.story_structure;

    const scenes = claudeData.scenes || [];
    const productionScenes = gptData.scenes || [];

    const tabs = [
        {
            key: "characters",
            label: "Character Bible",
            available: !!characters,
        },
        {
            key: "locations",
            label: "Location Bible",
            available: !!locations,
        },
        {
            key: "story",
            label: "Story Structure",
            available: !!story,
        },
        {
            key: "scenes",
            label: "Scene Breakdown",
            available: scenes.length > 0,
        },
        // {
        //     key: "character-prompts",
        //     label: "Character Prompts",
        //     available:
        //         Array.isArray(resultData?.character_image_prompts) &&
        //         resultData.character_image_prompts.length > 0,
        // },
        // {
        //     key: "location-prompts",
        //     label: "Location Prompts",
        //     available:
        //         Array.isArray(resultData?.location_image_prompts) &&
        //         resultData.location_image_prompts.length > 0,
        // },
        {
            key: "prompts",
            label: "Video Prompts",
            available: productionScenes.length > 0,
        },
    ].filter((tab) => tab.available);

    const tabSignature = tabs.map((tab) => tab.key).join("|");

    useEffect(() => {
        if (tabs.length && !tabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(tabs[0].key);
        }
    }, [resultData, tabSignature]);

    const title =
        claudeData.video_title ||
        gptData.video_title ||
        "Generated Advertisement";

    const duration =
        claudeData.total_duration_seconds ||
        gptData.total_duration_seconds;

    const isImageTab = activeTab === "characters" || activeTab === "locations";

    return (
        <div className="advideo-result-container">
            <div className="advideo-result-hero">
                <div>
                    <span className="advideo-result-kicker">
                        ADVERTISEMENT BLUEPRINT
                    </span>
                    <h2>{title}</h2>
                </div>

                <div className="advideo-status-pill">
                    <span />
                    Generation Complete
                </div>
            </div>

            {tabs.length > 0 && (
                <div className="advideo-tabs" role="tablist">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={activeTab === tab.key ? "active" : ""}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="advideo-tab-content">
                {activeTab === "characters" && (
                    <CharacterBible
                        characters={characters}
                        onGenerate={onGenerateBibleImage}
                        generating={generatingCharacter}
                        characterImage={characterImage}
                        aspectRatio={aspectRatio}
                        setAspectRatio={setAspectRatio}
                        prompt={formatImagePromptText(
                            resultData?.character_image_prompts
                        )}
                        showPromptModal={showPromptModal}
                        setShowPromptModal={setShowPromptModal}
                    />
                )}

                {activeTab === "locations" && (
                    <LocationBible
                        locations={locations}
                        onGenerate={onGenerateBibleImage}
                        generating={generatingLocation}
                        locationImage={locationImage}
                        aspectRatio={aspectRatio}
                        setAspectRatio={setAspectRatio}
                        prompt={formatImagePromptText(
                            resultData?.location_image_prompts
                        )}
                        showPromptModal={showPromptModal}
                        setShowPromptModal={setShowPromptModal}
                    />
                )}

                {activeTab === "story" && <StoryStructure story={story} />}

                {activeTab === "scenes" && (
                    <SceneList
                        scenes={scenes}
                        extraKey="character_actions"
                        emptyText="No scene breakdown available."
                        sceneAspectRatios={sceneAspectRatios}
                        setSceneAspectRatios={setSceneAspectRatios}
                        activeTab={activeTab}
                    />
                )}

                {activeTab === "prompts" && (
                    <SceneList
                        scenes={productionScenes}
                        extraKey="video_prompt"
                        production
                        emptyText="No Video Prompts available."
                        sceneAspectRatios={sceneAspectRatios}
                        setSceneAspectRatios={setSceneAspectRatios}
                        activeTab={activeTab}
                    />
                )}

                {activeTab === "character-prompts" && (
                    <PromptCollection
                        title="Character Image Prompts"
                        prompts={resultData.character_image_prompts}
                        type="Character"
                    />
                )}

                {activeTab === "location-prompts" && (
                    <PromptCollection
                        title="Location Image Prompts"
                        prompts={resultData.location_image_prompts}
                        type="Location"
                    />
                )}
            </div>

            <div className="gradiant-text text-center">
                {isImageTab &&
                    (generatingCharacter || generatingLocation) && (
                        <p className="advideo-model-note">
                            Generating with gpt-image-2.5-sunburst.
                        </p>
                    )
                }
            </div>
            <div className="advideo-edit-container">
                {(
                    (activeTab === "characters" &&
                        !generatingCharacter &&
                        characterImage) ||
                    (activeTab === "locations" &&
                        !generatingLocation &&
                        locationImage)
                ) && (
                        <button
                            type="button"
                            className="advideo-edit-button"
                            disabled
                        >
                            Edit
                        </button>
                    )}
            </div>
            {["characters", "locations", "prompts"].includes(activeTab) && (
                <div className="advideo-tip-banner">
                    <div className="advideo-tip-icon">
                        <span>✦</span>
                    </div>

                    <div className="advideo-tip-content">
                        {/* <h4>Pro Tip</h4> */}
                        <p>
                            Copy the prompt and use it with any video generation model,
                            or click <strong>Generate</strong> to create your video
                            using our built-in AI video model.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

/* =====================================================
   MAIN PAGE
===================================================== */

export default function AdVideoAi() {
    const { AIuser } = useContext(UserAuthContext);

    const navigate = useNavigate();

    // Read once, before the first render, so a reload restores everything.
    // const [persisted] = useState(loadPersistedState);

    // const [formData, setFormData] = useState({
    //     ...initialFormState,
    //     ...(persisted.formData || {}),
    // });
    // const [isLoading, setIsLoading] = useState(hasActiveTransactionInStorage);
    // const [resultData, setResultData] = useState(persisted.resultData || null);
    // const [transactionId, setTransactionId] = useState(
    //     persisted.transactionId || null
    // );

    // // Sessions are loaded to keep the existing /sessions request intact.
    // // eslint-disable-next-line no-unused-vars
    // const [sessions, setSessions] = useState([]);
    // // eslint-disable-next-line no-unused-vars
    // const [loadingSessions, setLoadingSessions] = useState(false);

    // const pollingRef = useRef(null);
    // const checkingRef = useRef(false);
    // const promptTimeoutRef = useRef(null);
    // const promptRequestRef = useRef(null);

    // const [suggestedPrompt, setSuggestedPrompt] = useState("");
    // const [backendSuggestions, setBackendSuggestions] = useState(null);
    // const [checkingPrompt, setCheckingPrompt] = useState(false);

    // const [characterImage, setCharacterImage] = useState(
    //     persisted.characterImage || null
    // );
    // const [locationImage, setLocationImage] = useState(
    //     persisted.locationImage || null
    // );
    // const [restoredImageGen] = useState(() =>
    //     restoreImageGeneration(persisted.imageGeneration)
    // );
    // const [generatingCharacter, setGeneratingCharacter] = useState(
    //     Boolean(restoredImageGen.character)
    // );
    // const [generatingLocation, setGeneratingLocation] = useState(
    //     Boolean(restoredImageGen.location)
    // );

    // const [sessionId, setSessionId] = useState(persisted.sessionId || null);
    // const [imageGeneration, setImageGeneration] = useState(restoredImageGen);
    // const imageCheckingRef = useRef({ character: false, location: false });
    // const [aspectRatio, setAspectRatio] = useState(
    //     persisted.aspectRatio || "16:9"
    // );
    // const [sceneAspectRatios, setSceneAspectRatios] = useState(
    //     persisted.sceneAspectRatios || {}
    // );


    const [formData, setFormData] = useState({ ...initialFormState });

    const [isLoading, setIsLoading] = useState(hasActiveTransactionInStorage);

    const [resultData, setResultData] = useState(null);
    const [transactionId, setTransactionId] = useState(null);

    // Sessions
    // eslint-disable-next-line no-unused-vars
    const [sessions, setSessions] = useState([]);

    // eslint-disable-next-line no-unused-vars
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [showPromptModal, setShowPromptModal] = useState(false);

    const pollingRef = useRef(null);
    const checkingRef = useRef(false);
    const promptTimeoutRef = useRef(null);
    const promptRequestRef = useRef(null);

    const [suggestedPrompt, setSuggestedPrompt] = useState("");
    const [backendSuggestions, setBackendSuggestions] = useState(null);
    const [checkingPrompt, setCheckingPrompt] = useState(false);

    const [characterImage, setCharacterImage] = useState(null);
    const [locationImage, setLocationImage] = useState(null);

    const [generatingCharacter, setGeneratingCharacter] = useState(false);
    const [generatingLocation, setGeneratingLocation] = useState(false);

    const [sessionId, setSessionId] = useState(null);

    const [imageGeneration, setImageGeneration] = useState({
        character: null,
        location: null,
    });

    const imageCheckingRef = useRef({
        character: false,
        location: false,
    });

    const [aspectRatio, setAspectRatio] = useState("16:9");
    const [sceneAspectRatios, setSceneAspectRatios] = useState({});

    // Always points at the newest handlers so long-lived intervals and
    // window listeners never run against stale state.
    const latest = useRef({});

    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser.credits)).toLocaleString()
        : "0";

    const isFormValid =
        formData.promoting.trim() &&
        formData.targetAudience.trim() &&
        formData.keyMessage.trim() &&
        formData.action.trim() &&
        formData.visualStyle.trim();

    const hasAnyInput = Object.values(formData).some((value) =>
        value.trim()
    );

    /* ---------- polling / storage ---------- */

    const clearPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const saveActiveTransaction = (txId) => {
        if (!txId) return;

        localStorage.setItem(
            ACTIVE_STORAGE_KEY,
            JSON.stringify({
                transactionId: txId,
                aiType: "advertisement-video-ai",
                startedAt: Date.now(),
            })
        );
    };

    const removeActiveTransaction = () => {
        localStorage.removeItem(ACTIVE_STORAGE_KEY);
    };

    const resetImageState = () => {
        setCharacterImage(null);
        setLocationImage(null);
        setGeneratingCharacter(false);
        setGeneratingLocation(false);
        setImageGeneration({ character: null, location: null });
        setSessionId(null);

        // Release any in-flight status check from the previous advertisement.
        imageCheckingRef.current = { character: false, location: false };
    };

    /* ---------- mark viewed ---------- */

    const markViewed = async (txId) => {
        if (!txId) return;

        try {
            const response = await axios.post(
                `${AD_VIDEO_API}/mark-viewed`,
                { transactionId: txId },
                { headers: apiHeaders(), timeout: STATUS_REQUEST_TIMEOUT_MS }
            );

            debugResponse("MARK VIEWED RESPONSE", response);
        } catch (error) {
            debugError("MARK VIEWED ERROR", error);
        }
    };

    /* ---------- completed ---------- */

    // eslint-disable-next-line no-unused-vars
    const handleCompleted = async (data, source = "active-status") => {
        if (!data) return;

        const completedData = data?.data || data;

        if (!completedData) return;

        setResultData(completedData);
        setSessionId(
            completedData?.sessionId || data?.data?.sessionId || null
        );

        const txId =
            data?.transactionId ||
            completedData?.transactionId ||
            transactionId;

        if (txId && transactionId && txId !== transactionId) {
            setCharacterImage(null);
            setLocationImage(null);
        }

        // When the backend re-delivers a finished advertisement (page reload,
        // missed socket) it also carries the images generated so far.
        if (
            typeof completedData.currentCharacterImage === "string" &&
            completedData.currentCharacterImage.trim()
        ) {
            setCharacterImage(completedData.currentCharacterImage);
            setGeneratingCharacter(false);
        }

        if (
            typeof completedData.currentLocationImage === "string" &&
            completedData.currentLocationImage.trim()
        ) {
            setLocationImage(completedData.currentLocationImage);
            setGeneratingLocation(false);
        }

        setTransactionId(txId || null);
        setIsLoading(false);

        clearPolling();
        removeActiveTransaction();

        if (txId) {
            await markViewed(txId);
        }

        try {
            localStorage.setItem(
                UNVIEWED_STORAGE_KEY,
                JSON.stringify({
                    transactionId: txId,
                    data: completedData,
                    completedAt: Date.now(),
                })
            );
        } catch (error) {
            console.error(
                "[AD VIDEO AI] Failed to store unviewed result:",
                error
            );
        }

        loadSessions();
    };

    /* ---------- failed ---------- */

    const handleFailed = (data) => {
        console.group(
            "%c[AD VIDEO AI] GENERATION FAILED",
            "color:#ef4444;font-weight:700;"
        );
        console.log("Failed Data:", data);
        console.log("Transaction ID:", data?.transactionId);
        console.log("Message:", data?.message);
        console.log("Error:", data?.error);
        console.groupEnd();

        clearPolling();
        removeActiveTransaction();

        setIsLoading(false);

        swal({
            title: "Generation Failed",
            text:
                data?.message ||
                data?.error ||
                "Advertisement generation failed.",
            icon: "error",
            button: "Okay",
            className: "modal_class_success",
        });
    };

    /* ---------- active status ---------- */

    const checkActiveStatus = async () => {
        if (checkingRef.current) {
            return;
        }

        checkingRef.current = true;

        try {
            const response = await axios.get(
                `${AD_VIDEO_API}/active-status`,
                {
                    headers: apiHeaders(),
                    timeout: STATUS_REQUEST_TIMEOUT_MS,
                    // Safari caches GET responses aggressively.
                    params: { _: Date.now() },
                }
            );

            debugResponse("ACTIVE STATUS RESPONSE", response);

            const data = response?.data;

            if (!data?.success) {
                return;
            }

            // A character / location image is "pending" on the backend too.
            // That must not switch the page back to the advertisement
            // loading screen.
            const imageGen = latest.current.imageGeneration || {};
            const imageBusy = Boolean(imageGen.character || imageGen.location);

            if (data.isGenerating && imageBusy) {
                return;
            }

            if (data.status === "completed" || data?.data) {
                await handleCompleted(data, "active-status");
                return;
            }

            if (data.status === "failed" || data.status === "error") {
                handleFailed(data);
                return;
            }

            if (data.isGenerating) {
                setIsLoading(true);

                if (data.transactionId) {
                    setTransactionId(data.transactionId);
                    saveActiveTransaction(data.transactionId);
                }
            }
        } catch (error) {
            debugError("ACTIVE STATUS ERROR", error);
        } finally {
            checkingRef.current = false;
        }
    };

    const startPolling = () => {
        clearPolling();

        checkActiveStatus();

        pollingRef.current = setInterval(() => {
            latest.current.checkActiveStatus?.();
        }, 3000);
    };

    /* ---------- sessions ---------- */

    const loadSessions = async () => {
        try {
            setLoadingSessions(true);

            const response = await axios.get(`${AD_VIDEO_API}/sessions`, {
                headers: apiHeaders(),
                timeout: STATUS_REQUEST_TIMEOUT_MS * 2,
            });

            debugResponse("SESSIONS RESPONSE", response);

            if (response?.data?.success) {
                setSessions(response.data.sessions || []);
            }
        } catch (error) {
            debugError("SESSIONS ERROR", error);
        } finally {
            setLoadingSessions(false);
        }
    };

    /* ---------- generate advertisement ---------- */

    const handleGenerate = async () => {
        if (!isFormValid) {
            swal({
                text: "Please fill in all required fields.",
                icon: "warning",
                button: "Okay",
                className: "modal_class_success",
            });

            return;
        }

        try {
            setIsLoading(true);
            setResultData(null);
            setTransactionId(null);
            resetImageState();

            console.group(
                "%c[AD VIDEO AI] GENERATION REQUEST",
                "color:#f59e0b;font-weight:700;"
            );
            console.log("Request URL:", `${AD_VIDEO_API}/generate`);
            console.log("Request Body:", formData);
            console.groupEnd();

            const response = await axios.post(
                `${AD_VIDEO_API}/generate`,
                {
                    promoting: formData.promoting,
                    targetAudience: formData.targetAudience,
                    keyMessage: formData.keyMessage,
                    action: formData.action,
                    visualStyle: formData.visualStyle,
                    specialInstructions: formData.specialInstructions,
                },
                {
                    headers: {
                        ...apiHeaders(),
                        "Content-Type": "application/json",
                    },
                }
            );

            debugResponse("GENERATE RESPONSE", response);

            const data = response?.data;

            if (!response?.status || !data?.success) {
                throw new Error(
                    data?.message ||
                    "Failed to start advertisement generation."
                );
            }

            const txId = data?.transactionId;

            if (!txId) {
                throw new Error(
                    "Generation started but no transaction ID was returned."
                );
            }

            setTransactionId(txId);
            saveActiveTransaction(txId);
            startPolling();
        } catch (error) {
            debugError("GENERATE ERROR", error);

            setIsLoading(false);

            swal({
                text:
                    error?.response?.data?.message ||
                    error.message ||
                    "Something went wrong. Please try again.",
                icon: "error",
                button: "Okay",
                className: "modal_class_success",
            });
        }
    };

    /* ---------- suggestions ---------- */

    const changeSuggestedPrompt = () => {
        setSuggestedPrompt(
            AdVideoExamplePrompt[
            Math.floor(Math.random() * AdVideoExamplePrompt.length)
            ]
        );
    };

    const cancelPendingPromptCheck = () => {
        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }

        if (promptRequestRef.current) {
            promptRequestRef.current.abort();
            promptRequestRef.current = null;
        }
    };

    const checkPromotingPrompt = async (value) => {
        const prompt = value?.trim();

        if (!prompt || prompt.length < 3) {
            setBackendSuggestions(null);
            setCheckingPrompt(false);
            return;
        }

        if (promptRequestRef.current) {
            promptRequestRef.current.abort();
        }

        const controller = new AbortController();
        promptRequestRef.current = controller;

        try {
            setCheckingPrompt(true);

            const res = await axios.post(
                `${API_BASE}/prompt-checker/check`,
                {
                    prompt,
                    aiService: "ad-video-ai",
                },
                {
                    headers: apiHeaders(),
                    signal: controller.signal,
                }
            );

            if (promptRequestRef.current !== controller) {
                return;
            }

            if (res?.data?.success) {
                const data = res.data.data;
                setBackendSuggestions(data?.suggested_prompt || null);
            } else {
                setBackendSuggestions(null);
            }
        } catch (error) {
            if (error?.code !== "ERR_CANCELED") {
                console.error(
                    "[AD VIDEO AI] Prompt checker error:",
                    error?.response?.data || error
                );
                setBackendSuggestions(null);
            }
        } finally {
            if (promptRequestRef.current === controller) {
                promptRequestRef.current = null;
                setCheckingPrompt(false);
            }
        }
    };

    /* ---------- input ---------- */

    const handleInputChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleFieldChange = (key, value) => {
        handleInputChange(key, value);

        if (key !== "promoting") {
            return;
        }

        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }

        setBackendSuggestions(null);

        if (value.trim().length < 3) {
            if (promptRequestRef.current) {
                promptRequestRef.current.abort();
                promptRequestRef.current = null;
            }

            setCheckingPrompt(false);
            return;
        }

        promptTimeoutRef.current = setTimeout(() => {
            checkPromotingPrompt(value.trim());
        }, 700);
    };

    const applySuggestedPrompt = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const value = String(suggestedPrompt || "").trim();

        if (!value) return;

        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }

        handleInputChange("promoting", value);
        checkPromotingPrompt(value);
    };

    const applyBackendSuggestion = (key) => {
        const value = backendSuggestions?.[key];

        if (typeof value !== "string" || !value.trim()) {
            return;
        }

        if (promptTimeoutRef.current) {
            clearTimeout(promptTimeoutRef.current);
            promptTimeoutRef.current = null;
        }

        handleInputChange(key, value);
    };

    /* ---------- clear ---------- */

    const handleClear = async () => {
        const result = await Swal.fire({
            text: "Are you sure you want to clear?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            focusCancel: true,
            background: "#111827",
            color: "#f9fafb",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#374151",
        });

        if (!result.isConfirmed) return;

        setFormData(initialFormState);
        setResultData(null);
        setTransactionId(null);

        clearPolling();
        removeActiveTransaction();

        localStorage.removeItem(UNVIEWED_STORAGE_KEY);

        changeSuggestedPrompt();

        setBackendSuggestions(null);
        setCheckingPrompt(false);
        cancelPendingPromptCheck();

        resetImageState();
        setSceneAspectRatios({});

        // try {
        //     localStorage.removeItem(PERSIST_KEY);
        //     localStorage.removeItem(TAB_KEY);
        // } catch (error) {
        //     console.error("[AD VIDEO AI] Failed to clear saved state:", error);
        // }
        // Remove legacy page-state entries created by older versions.
        try {
            localStorage.removeItem("ad_video_ai_page_state");
            localStorage.removeItem("ad_video_ai_active_tab");
        } catch (error) {
            console.error("[AD VIDEO AI] Failed to clear legacy state:", error);
        }

        Swal.fire({
            title: "Cleared!",
            text: "All form inputs and generated results have been cleared.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            background: "#111827",
            color: "#f9fafb",
        });
    };

    /* ---------- character / location image generation ---------- */

    const handleGenerateBibleImage = async (type) => {
        if (!["character", "location"].includes(type)) return;

        if (!sessionId || !transactionId) {
            swal({
                icon: "warning",
                title: "Missing Session",
                text: "Please generate the advertisement content first.",
                className: "modal_class_success",
            });
            return;
        }

        if (
            (type === "character" && generatingCharacter) ||
            (type === "location" && generatingLocation)
        ) {
            return;
        }

        if (type === "character") {
            setCharacterImage(null);
            setGeneratingCharacter(true);
        } else {
            setLocationImage(null);
            setGeneratingLocation(true);
        }

        // Make sure an old, stuck status check can never block this new one.
        imageCheckingRef.current[type] = false;

        // taskId is added once create-image answers; polling starts then.
        setImageGeneration((prev) => ({
            ...prev,
            [type]: {
                transactionId,
                sessionId,
                type,
                taskId: null,
                baseline: null,
                startedAt: Date.now(),
            },
        }));

        try {
            const response = await axios.post(
                `${AD_VIDEO_API}/create-image`,
                {
                    sessionId,
                    type,
                    transactionId,
                    aspectRatio,
                },
                {
                    headers: apiHeaders(),
                    timeout: 120000,
                }
            );

            if (!response.data?.success) {
                throw new Error(
                    response.data?.message ||
                    "Image generation could not start."
                );
            }

            // If the WebSocket already delivered the image, the entry is gone
            // and must not be recreated (it would start a pointless poll).
            setImageGeneration((prev) => {
                if (!prev[type]) return prev;

                return {
                    ...prev,
                    [type]: {
                        ...prev[type],
                        transactionId:
                            response.data.transactionId || transactionId,
                        sessionId: response.data.sessionId || sessionId,
                        taskId: response.data.transactionId || transactionId,
                        // How many images of this type existed before this
                        // request; the status endpoint reports "completed"
                        // once a newer one has been saved.
                        baseline: Number.isFinite(response.data.imageCount)
                            ? response.data.imageCount
                            : null,
                        status: "pending",
                    },
                };
            });
        } catch (error) {
            console.error(
                `[AD VIDEO AI] ${type} image generation error:`,
                error
            );

            if (type === "character") {
                setGeneratingCharacter(false);
                setCharacterImage(null);
            } else {
                setGeneratingLocation(false);
                setLocationImage(null);
            }

            setImageGeneration((prev) => ({
                ...prev,
                [type]: null,
            }));

            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Unable to generate image. Please try again.";

            const isSafetyError =
                error.response?.data?.code === "moderation_blocked" ||
                error.response?.data?.error?.code === "moderation_blocked" ||
                String(errorMessage).toLowerCase().includes("safety system") ||
                String(errorMessage).toLowerCase().includes("moderation_blocked");

            swal({
                icon: "error",
                title: isSafetyError
                    ? "Image Generation Blocked"
                    : "Generation Failed",
                text: isSafetyError
                    ? "The image request was blocked by the AI safety system. Please revise the prompt and try again."
                    : errorMessage,
                confirmButtonText: "OK",
                className: "modal_class_success",
            });
        }
    };

    const finishImageGeneration = (type) => {
        if (type === "character") {
            setGeneratingCharacter(false);
        } else {
            setGeneratingLocation(false);
        }

        setImageGeneration((prev) => ({
            ...prev,
            [type]: null,
        }));
    };

    // Fallback for a missed WebSocket event (Safari suspends/drops sockets).
    const checkImageStatus = async (type, taskId) => {
        const current = latest.current.imageGeneration?.[type];

        // Stale poll: this generation was finished, cleared or replaced.
        if (!current || current.taskId !== taskId) return;

        if (imageCheckingRef.current[type]) return;

        if (
            current.startedAt &&
            Date.now() - current.startedAt > IMAGE_TIMEOUT_MS
        ) {
            finishImageGeneration(type);

            swal({
                icon: "warning",
                title: "Taking longer than expected",
                text: `The ${type} image is still not ready. Please try again, or check your Usage page later.`,
                confirmButtonText: "OK",
                className: "modal_class_success",
            });

            return;
        }

        imageCheckingRef.current[type] = true;

        try {
            const response = await axios.get(
                `${AD_VIDEO_API}/image-generation-status/${taskId}`,
                {
                    headers: apiHeaders(),
                    timeout: STATUS_REQUEST_TIMEOUT_MS,
                    params: {
                        sessionId: current.sessionId,
                        type,
                        ...(Number.isFinite(current.baseline)
                            ? { after: current.baseline }
                            : {}),
                        // Safari caches GET responses aggressively.
                        _: Date.now(),
                    },
                }
            );

            const body = response?.data || {};
            const payload =
                body.data && typeof body.data === "object" ? body.data : body;
            const status = payload.status || body.status;
            const imageUrl = payload.imageUrl || body.imageUrl;

            // The WebSocket may have finished it while we were waiting.
            if (latest.current.imageGeneration?.[type]?.taskId !== taskId) {
                return;
            }

            if (status === "completed" && imageUrl) {
                if (type === "character") {
                    setCharacterImage(imageUrl);
                } else {
                    setLocationImage(imageUrl);
                }

                finishImageGeneration(type);
            } else if (status === "failed") {
                finishImageGeneration(type);

                swal({
                    icon: "error",
                    title: "Image Generation Failed",
                    text:
                        payload.message ||
                        body.message ||
                        "Unable to generate the image. Please try again.",
                    confirmButtonText: "OK",
                    className: "modal_class_success",
                });
            }
        } catch (error) {
            console.error("[AD VIDEO AI] Image status check failed:", error);
        } finally {
            imageCheckingRef.current[type] = false;
        }
    };

    const checkPendingImages = () => {
        const generation = latest.current.imageGeneration || {};

        ["character", "location"].forEach((type) => {
            const taskId = generation[type]?.taskId;

            if (taskId) {
                checkImageStatus(type, taskId);
            }
        });
    };

    const handleImageCompleted = (event) => {
        let data = event?.detail ?? event;

        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch {
                console.error("Invalid image completion message");
                return;
            }
        }

        if (
            data?.type !== "advertisement-video-ai" ||
            data?.action !== "ad_video_ai_image_completed"
        ) {
            return;
        }

        const imageData = data?.data || {};
        const imageType = imageData.imageType || data.imageType;
        const imageUrl = imageData.imageUrl || data.imageUrl;

        if (
            !["character", "location"].includes(imageType) ||
            typeof imageUrl !== "string" ||
            !imageUrl.trim()
        ) {
            console.error("Invalid image completion payload:", data);
            return;
        }

        if (imageType === "character") {
            setCharacterImage(imageUrl);
            setGeneratingCharacter(false);
        } else {
            setLocationImage(imageUrl);
            setGeneratingLocation(false);
        }

        setImageGeneration((prev) => ({
            ...prev,
            [imageType]: null,
        }));
    };

    /* ---------- keep latest handlers reachable from listeners ---------- */

    latest.current = {
        checkActiveStatus,
        handleCompleted,
        handleFailed,
        handleImageCompleted,
        checkImageStatus,
        checkPendingImages,
        imageGeneration,
    };

    const characterTaskId = imageGeneration.character?.taskId || null;
    const locationTaskId = imageGeneration.location?.taskId || null;

    /* =====================================================
       EFFECTS
    ===================================================== */

    // Poll character image status while it is pending
    useEffect(() => {
        if (!characterTaskId) return undefined;

        // First check right away instead of waiting a full interval.
        const firstCheck = setTimeout(() => {
            latest.current.checkImageStatus("character", characterTaskId);
        }, 1500);

        const intervalId = setInterval(() => {
            latest.current.checkImageStatus("character", characterTaskId);
        }, IMAGE_POLL_MS);

        return () => {
            clearTimeout(firstCheck);
            clearInterval(intervalId);
        };
    }, [characterTaskId]);

    // Poll location image status while it is pending
    useEffect(() => {
        if (!locationTaskId) return undefined;

        const firstCheck = setTimeout(() => {
            latest.current.checkImageStatus("location", locationTaskId);
        }, 1500);

        const intervalId = setInterval(() => {
            latest.current.checkImageStatus("location", locationTaskId);
        }, IMAGE_POLL_MS);

        return () => {
            clearTimeout(firstCheck);
            clearInterval(intervalId);
        };
    }, [locationTaskId]);

    // Save inputs + advertisement state so a reload restores the page
    // useEffect(() => {
    //     try {
    //         localStorage.setItem(
    //             PERSIST_KEY,
    //             JSON.stringify({
    //                 formData,
    //                 resultData,
    //                 transactionId,
    //                 sessionId,
    //                 characterImage,
    //                 locationImage,
    //                 imageGeneration,
    //                 aspectRatio,
    //                 sceneAspectRatios,
    //             })
    //         );
    //     } catch (error) {
    //         console.error("[AD VIDEO AI] Failed to save page state:", error);
    //     }
    // }, [
    //     formData,
    //     resultData,
    //     transactionId,
    //     sessionId,
    //     characterImage,
    //     locationImage,
    //     imageGeneration,
    //     aspectRatio,
    //     sceneAspectRatios,
    // ]);

    // Rotating example prompt
    useEffect(() => {
        changeSuggestedPrompt();

        const intervalId = setInterval(() => {
            changeSuggestedPrompt();
        }, 5000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial status check / resume of an active transaction
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "instant",
        });

        const initialize = async () => {
            console.group(
                "%c[AD VIDEO AI] INITIALIZING",
                "color:#38bdf8;font-weight:700;"
            );
            console.log(
                "Active storage:",
                localStorage.getItem(ACTIVE_STORAGE_KEY)
            );
            console.groupEnd();

            const activeStr = localStorage.getItem(ACTIVE_STORAGE_KEY);

            if (activeStr) {
                try {
                    const active = JSON.parse(activeStr);

                    if (active?.transactionId) {
                        setTransactionId(active.transactionId);
                        setIsLoading(true);
                        startPolling();
                        return;
                    }
                } catch (error) {
                    console.error(
                        "[AD VIDEO AI] Invalid active transaction:",
                        error
                    );

                    removeActiveTransaction();
                }
            }

            // await checkActiveStatus();

            loadSessions();
        };

        initialize();

        return () => {
            clearPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // WebSocket-driven window events (registered once, always call latest handlers)
    useEffect(() => {
        const handleBackendCompleted = (event) => {
            const data = event?.detail;

            console.log("[AD VIDEO AI] COMPLETION EVENT RECEIVED:", data);

            if (!data) return;

            if (
                data?.type === "advertisement-video-ai" &&
                data?.action === "ad_video_ai_image_completed"
            ) {
                latest.current.handleImageCompleted(data);
                return;
            }

            if (
                data?.type === "advertisement-video-ai" &&
                data?.action === "ad_video_ai_completed"
            ) {
                latest.current.handleCompleted(data, "websocket/event");
            }
        };

        const handleBackendFailed = (event) => {
            const data = event?.detail;

            console.log("[AD VIDEO AI] BACKEND FAILED EVENT:", data);

            if (
                data?.type !== "advertisement-video-ai" ||
                data?.action !== "ad_video_ai_failed"
            ) {
                return;
            }

            // Image-generation failure is handled separately
            if (data?.subType === "image-generation") {
                const imageType = data?.data?.imageType || data?.imageType;

                if (imageType === "character") {
                    setGeneratingCharacter(false);
                    setCharacterImage(null);
                } else if (imageType === "location") {
                    setGeneratingLocation(false);
                    setLocationImage(null);
                }

                if (imageType === "character" || imageType === "location") {
                    setImageGeneration((prev) => ({
                        ...prev,
                        [imageType]: null,
                    }));
                }

                swal({
                    icon: "error",
                    title: "Image Generation Failed",
                    text:
                        data?.message ||
                        "Unable to generate the image. Please try again.",
                    confirmButtonText: "OK",
                    className: "modal_class_success",
                });

                return;
            }

            // Normal advertisement-generation failure
            if (data?.status === "failed") {
                latest.current.handleFailed(data);
            }
        };

        window.addEventListener("backend_task_completed", handleBackendCompleted);
        window.addEventListener("backend_task_failed", handleBackendFailed);

        return () => {
            window.removeEventListener(
                "backend_task_completed",
                handleBackendCompleted
            );
            window.removeEventListener(
                "backend_task_failed",
                handleBackendFailed
            );
        };
    }, []);

    // Resume status check when the tab wakes up
    useEffect(() => {
        const hasActiveTransaction = () => {
            try {
                const raw = localStorage.getItem(ACTIVE_STORAGE_KEY);

                if (!raw) return false;

                const active = JSON.parse(raw);

                return Boolean(
                    active?.transactionId &&
                    active?.aiType === "advertisement-video-ai"
                );
            } catch (error) {
                console.error(
                    "[AD VIDEO AI] Active transaction check failed:",
                    error
                );

                return false;
            }
        };

        const handleWakeUp = () => {
            if (document.visibilityState !== "visible") {
                return;
            }

            // Safari may have dropped the socket while the tab was hidden.
            latest.current.checkPendingImages();

            if (!hasActiveTransaction()) {
                return;
            }

            console.log("[AD VIDEO AI] Resuming existing transaction...");

            latest.current.checkActiveStatus();
        };

        document.addEventListener("visibilitychange", handleWakeUp);
        window.addEventListener("focus", handleWakeUp);
        window.addEventListener("pageshow", handleWakeUp);

        return () => {
            document.removeEventListener("visibilitychange", handleWakeUp);
            window.removeEventListener("focus", handleWakeUp);
            window.removeEventListener("pageshow", handleWakeUp);
        };
    }, []);

    // Cancel pending prompt-checker work on unmount
    useEffect(() => {
        return () => {
            if (promptTimeoutRef.current) {
                clearTimeout(promptTimeoutRef.current);
            }

            if (promptRequestRef.current) {
                promptRequestRef.current.abort();
            }
        };
    }, []);

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <Fragment>
            <div className="advideo-page">
                <div className="container">
                    {/* ---------- Title + credits ---------- */}
                    <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
                        <div>
                            <p className="all-page-title all-page-gradient-text text-center my-4">
                                AD VIDEO AI
                            </p>

                            <div className="credits-actions">
                                <button className="credits-button">
                                    CREDITS : {creditToLocalString}
                                </button>

                                <button
                                    className="buy-credits-button"
                                    onClick={() => navigate("/purchase")}
                                >
                                    BUY CREDITS
                                </button>

                                <button
                                    onClick={() => navigate("/usage")}
                                    className="money-exchange-button"
                                >
                                    <img src={moneyExchangeIcon} alt="usage" />
                                </button>

                                <button
                                    type="button"
                                    className="money-exchange-button"
                                    onClick={() => {
                                        swal({
                                            title: "Coming Soon",
                                            text: "Sample video feature is coming soon.",
                                            button: "Okay",
                                            className: "modal_class_success",
                                        });
                                    }}
                                >
                                    <img src={sampleIcon} alt="See sample video" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ---------- Intro ---------- */}
                    <section className="advideo-intro">
                        <h1>
                            Build Your Ad
                            <br />
                            <span className="gradiant-text">From Idea</span>
                        </h1>

                        <p>
                            Turn your ad ideas into stunning video
                            advertisements instantly with WhalesAI Ad Video AI.
                        </p>
                    </section>

                    {/* ---------- Form ---------- */}
                    <section className="advideo-form-card">
                        <div className="advideo-form-header">
                            <span className="advideo-kicker">STEP-BY-STEP</span>
                            <button
                                type="button"
                                className="advideo-clear-link"
                                onClick={handleClear}
                                disabled={!hasAnyInput || isLoading}
                            >
                                Clear All
                            </button>
                        </div>
                        <p
                            style={{
                                fontSize: "12px",
                                marginBottom: "28px"
                            }}
                            className="advideo-form-sub-header"
                        >
                            Please feel free to review and edit any of the suggestions as you see fit.
                        </p>

                        <div className="advideo-form-grid">
                            {formQuestions.map((question, index) => {
                                const fieldId = `advideo-field-${question.key}`;
                                const backendValue =
                                    backendSuggestions?.[question.key];

                                return (
                                    <div
                                        className={`advideo-form-group ${question.isTextarea ? "full" : ""
                                            }`}
                                        key={question.key}
                                    >
                                        <div className="advideo-label-row">
                                            <div className="advideo-step-number">
                                                {String(index + 1).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </div>

                                            <label htmlFor={fieldId}>
                                                {question.label}

                                                {question.required ? (
                                                    <span className="required">
                                                        *
                                                    </span>
                                                ) : (
                                                    <span className="optional">
                                                        Optional
                                                    </span>
                                                )}
                                            </label>
                                        </div>

                                        <textarea
                                            id={fieldId}
                                            className="advideo-input advideo-textarea"
                                            rows={question.isTextarea ? 4 : 3}
                                            placeholder={question.placeholder}
                                            value={formData[question.key] || ""}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    question.key,
                                                    e.target.value
                                                )
                                            }
                                            disabled={isLoading}
                                        />

                                        {question.key === "promoting" && (
                                            <>
                                                {checkingPrompt && (
                                                    <div
                                                        className="advideo-checking"
                                                        role="status"
                                                    >
                                                        <span
                                                            className="advideo-spinner is-small"
                                                            aria-hidden="true"
                                                        />
                                                        <span>
                                                            Generating suggestions...
                                                        </span>
                                                    </div>
                                                )}

                                                {!checkingPrompt && (
                                                    <div className="advideo-suggestion-row">
                                                        <div className="advideo-suggestion-text">
                                                            <span className="advideo-suggestion-label">
                                                                Suggested:{" "}
                                                            </span>

                                                            <button
                                                                type="button"
                                                                className="advideo-suggested-prompt"
                                                                onClick={
                                                                    applySuggestedPrompt
                                                                }
                                                            >
                                                                {suggestedPrompt}
                                                            </button>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="advideo-refresh-suggestion"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                changeSuggestedPrompt();
                                                            }}
                                                            title="Generate another suggestion"
                                                            aria-label="Generate another suggestion"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-hidden="true"
                                                            >
                                                                <path d="M20 7v5h-5" />
                                                                <path d="M4 17v-5h5" />
                                                                <path d="M5.64 9A7 7 0 0 1 17.5 6.5L20 12" />
                                                                <path d="M18.36 15A7 7 0 0 1 6.5 17.5L4 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {backendValue && (
                                            <button
                                                type="button"
                                                className="advideo-idea-card"
                                                onClick={() =>
                                                    applyBackendSuggestion(
                                                        question.key
                                                    )
                                                }
                                            >
                                                <span className="advideo-idea-label">
                                                    Suggested Idea:
                                                </span>
                                                <span className="advideo-idea-text">
                                                    {backendValue}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="advideo-form-footer">
                            <div className="advideo-footer-note">
                                <span aria-hidden="true">✦</span>
                                With WhalesAI Ad Video AI, create your
                                advertisement step by step, reviewing and
                                approving each stage before moving forward.
                            </div>

                            <Button
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={!isFormValid || isLoading}
                                className="advideo-btn advideo-btn-primary advideo-generate-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <span className="advideo-spinner is-small" />
                                        GENERATING
                                    </>
                                ) : (
                                    <>
                                        GENERATE ADVERTISEMENT
                                        <span aria-hidden="true">→</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </section>

                    {/* ---------- Result area ---------- */}
                    {isLoading && <LoadingState />}

                    {!isLoading && !resultData && <EmptyState />}

                    {!isLoading && resultData && (
                        <ResultDashboard
                            resultData={resultData}
                            onGenerateBibleImage={handleGenerateBibleImage}
                            generatingCharacter={generatingCharacter}
                            generatingLocation={generatingLocation}
                            characterImage={characterImage}
                            locationImage={locationImage}
                            aspectRatio={aspectRatio}
                            setAspectRatio={setAspectRatio}
                            sceneAspectRatios={sceneAspectRatios}
                            setSceneAspectRatios={setSceneAspectRatios}
                            showPromptModal={showPromptModal}
                            setShowPromptModal={setShowPromptModal}
                        />
                    )}
                </div>
            </div>
        </Fragment>
    );
}

